import React, { useState, useEffect } from 'react';
import {
  useGetPredictionsStatus,
  useIsChartPaneOpen,
  useIsHistoryPaneOpen,
  useGetLastOraclePrice,
  useGetCurrentEpoch,
  useGetRound
} from './context/PredictionsContext';
import TradingView from './components/TradingView';
import History from './History';
import Positions from './Positions';
import Chart from './Chart';
import ErrorNotification from './components/Notification/ErrorNotification';
import PauseNotification from './components/Notification/PauseNotification';
import MobileMenu from './components/MobileMenu';
import { FaHistory, FaCopy, FaClock, FaChartLine, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { SiTether } from 'react-icons/si';
import bitcoin from '../../assets/utility/bitcoin.png';
import { useUSDTBalance } from './hooks/useUSDTBalance';
import { useBullvsBearContract } from './hooks/useBullvsBearContract';
import { useAccount } from 'wagmi';
import CountdownTimer from './components/CountdownTimer';
import { useSearchParams } from 'react-router-dom';

const Mobile = () => {
  const isHistoryPaneOpen = useIsHistoryPaneOpen();
  const isChartPaneOpen = useIsChartPaneOpen();
  const status = useGetPredictionsStatus();
  const lastOraclePrice = useGetLastOraclePrice?.();
  const currentEpoch = useGetCurrentEpoch?.();
  const currentRound = useGetRound(currentEpoch);
  const { address } = useAccount();
  const { getUSDTBalance } = useUSDTBalance();
  const { fetchContractData } = useBullvsBearContract();
  const [searchParams] = useSearchParams(); 
  const [referralAddress, setReferralAddress] = useState(null);
  // Referral state
  const [copied, setCopied] = useState(false);
  const [referralLink, setReferralLink] = useState("");
  const [usdtBalance, setUsdtBalance] = useState("0.00");

    useEffect(() => {
    const refParam = searchParams.get('ref');
    if (refParam && refParam !== address) {
      setReferralAddress(refParam);
    }
  }, [searchParams, address]);

  useEffect(() => {
    // Set referral link with user's address
    if (address) {
      setReferralLink(`${window.location.origin}/#/prediction?ref=${address}`);

      // Fetch USDT balance
      const fetchBalance = async () => {
        const balance = await getUSDTBalance(address);
        setUsdtBalance(balance);
      };

      fetchBalance();

      // Fetch contract data
      fetchContractData();
    }
  }, [address, getUSDTBalance, fetchContractData]);


  const handleCopy = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  // Winner logic (optional, to display a badge)
  let winner = null;
  let winnerStatus = null;

  if (currentRound && lastOraclePrice) {
    if (currentRound.closePrice && currentRound.lockPrice) {
      const closePrice = Number(currentRound.closePrice);
      const lockPrice = Number(currentRound.lockPrice);

      if (closePrice > lockPrice) {
        winner = 'UP';
        winnerStatus = 'win';
      } else if (closePrice < lockPrice) {
        winner = 'DOWN';
        winnerStatus = 'win';
      } else {
        winner = 'TIE';
        winnerStatus = 'tie';
      }
    } else if (currentRound.lockPrice) {
      const currentPrice = Number(lastOraclePrice);
      const lockPrice = Number(currentRound.lockPrice);

      if (currentPrice > lockPrice) {
        winner = 'UP';
        winnerStatus = 'live';
      } else if (currentPrice < lockPrice) {
        winner = 'DOWN';
        winnerStatus = 'live';
      } else {
        winner = 'TIE';
        winnerStatus = 'live';
      }
    }
  }

  const getView = () => {
    if (isHistoryPaneOpen) return 'history';
    if (isChartPaneOpen) return 'chart';
    return 'positions';
  };

  const view = getView();

  return (
    <div className="flex flex-col min-h-screen lg:hidden bg-[#18181b]">
      {/* Mobile Header */}
      <div className="sticky top-0 z-1 p-4 space-y-4 bg-[#18181b] border-b border-[#222]">
        {/* Referral Card */}
        <div className="bg-gradient-to-r from-[#2a2a2d] to-[#222] rounded-xl p-3 shadow-lg">
          <div className="space-y-3">
            <span className="text-white font-semibold flex items-center gap-2 text-sm">
              <FaHistory className="text-[#7900ff]" />
              Your referral link
            </span>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={referralLink}
                readOnly
                className="bg-[#18181b] text-gray-200 px-3 py-2 rounded-l-md border border-[#333] w-full text-sm focus:outline-none"
              />
              <button
                onClick={handleCopy}
                className="bg-[#7900ff] hover:bg-[#a259ff] text-white px-4 py-2 rounded-r-md font-bold transition-colors flex items-center gap-2 text-sm whitespace-nowrap"
              >
                <FaCopy size={12} />
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <div className="flex justify-between items-center pt-2 border-t border-[#333]">
              <span className="text-gray-400 text-sm flex items-center gap-1">
                <SiTether className="text-[#26A17B]" /> USDT Balance
              </span>
              <span className="text-lg font-bold text-green-400">
                {usdtBalance}
              </span>
            </div>
          </div>
        </div>

        {/* Info Cards Row */}
        <div className="flex items-center justify-between gap-2">
          {/* BTC Price Card */}
          <div className="flex items-center gap-3 bg-[#222] px-3 py-2 rounded-xl flex-1">
            <img src={bitcoin} alt="Bitcoin" className="w-6 h-6" />
            <div className="flex flex-col">
              <span className="text-xs text-gray-400">BTC/USDT</span>
              <span className="text-base font-bold text-[#F7931A]">
                {lastOraclePrice ? `$${Number(lastOraclePrice).toLocaleString()}` : '--'}
              </span>
            </div>
          </div>

          {/* Round Info with Countdown */}
          <div className="flex items-center gap-2 bg-[#7900ff] px-3 py-2 rounded-xl">
            <FaClock className="text-white text-sm" />
            <span className="text-white text-sm font-medium">
              #{currentEpoch ?? '--'}
            </span>
            {/* Countdown Timer for mobile */}
            <CountdownTimer status="live" type="close" />
          </div>
        </div>

        {/* Winner Indicator (optional, can be removed or styled differently on mobile) */}
        {winner && (
          <div className="px-4 py-2 rounded-xl font-bold flex items-center justify-content-center gap-2 bg-transparent border border-gray-700/50">
            {winner === 'UP' && <FaArrowUp className="text-green-500" />}
            {winner === 'DOWN' && <FaArrowDown className="text-red-500" />}
            {winner === 'TIE' && null}

            <span className={`${
              winner === 'UP' 
                ? 'text-green-500' 
                : winner === 'DOWN' 
                ? 'text-red-500' 
                : 'text-yellow-500'
            }`}>
              {winner === 'UP' && 'UP'}
              {winner === 'DOWN' && 'DOWN'}
              {winner === 'TIE' && '⚊ TIE'}
            </span>

            {/* Indicador de status - colorido também */}
            {winnerStatus === 'live' && (
              <div className={`w-2 h-2 rounded-full animate-pulse ml-1 ${
                winner === 'UP' 
                  ? 'bg-green-500' 
                  : winner === 'DOWN' 
                  ? 'bg-red-500' 
                  : 'bg-yellow-500'
              }`}></div>
            )}
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 relative"
        style={{
          paddingBottom: '80px', 
          minHeight: 'calc(100vh - 200px)' 
        }}>
        {/* Positions View */}
        <div
          className={`absolute inset-0 w-full transition-opacity duration-300 ${view === 'positions' ? 'opacity-100 z-10' : 'opacity-0 z-0'
            }`}
        >
          <div className="h-full bg-black px-2 pt-2 pb-25 flex flex-col overflow-x-hidden overflow-y-auto custom-scrollbar">
            {status === 'error' && <ErrorNotification />}
            {status === 'paused' && <PauseNotification />}
            {status === 'live' && (
              <div className="flex-1 flex flex-col h-full">
                <div className="bg-black rounded-xl shadow-lg p-3 h-full">
                  <Positions view={view} referralAddress={referralAddress} />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Chart View */}
        <div
          className={`absolute inset-0 w-full transition-opacity duration-300 ${view === 'chart' ? 'opacity-100 z-10' : 'opacity-0 z-0'
            }`}
        >
          <Chart />
        </div>

        {/* History View */}
        <div
          className={`absolute inset-0 w-full transition-opacity duration-300 ${view === 'history' ? 'opacity-100 z-10' : 'opacity-0 z-0'
            }`}
          style={{
            /* CORREÇÃO: Definir altura específica para mobile */
            height: 'calc(100vh - 200px)',
            overflow: 'hidden'
          }}
        >
          <div style={{ height: '100%', overflow: 'auto' }}>
            <History />
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <MobileMenu />
    </div>
  );
};

export default Mobile;